import { i18n } from '../i18n'

/**
 * Maps a refund status to its pale Vuetify background color class. Paired with
 * getRefundStatusTextColor to render status chips with a tinted background and a
 * saturated label (same convention as the trigger history table). Single source of
 * truth shared by the central refunds list (RefundTable.vue) and the order detail
 * "Refunds" tab (OrderDetails.vue).
 *
 * @param {string} status - The refund status (pending | refunded | failed).
 * @returns {string} The Vuetify background color class.
 */
export function getRefundStatusBackgroundColor(status) {
  switch (status) {
    case 'pending':
      return 'orange lighten-5'
    case 'refunded':
      return 'green lighten-5'
    case 'failed':
      return 'red lighten-5'
    default:
      return 'grey lighten-3'
  }
}

/**
 * Maps a refund status to its saturated Vuetify text color class, paired with
 * getRefundStatusBackgroundColor for the status chip label.
 *
 * @param {string} status - The refund status (pending | refunded | failed).
 * @returns {string} The Vuetify text color class.
 */
export function getRefundStatusTextColor(status) {
  switch (status) {
    case 'pending':
      return 'orange darken-4'
    case 'refunded':
      return 'green darken-4'
    case 'failed':
      return 'red darken-1'
    default:
      return 'grey darken-4'
  }
}

/**
 * Returns the translated refund statuses (text + value), used both as
 * FilterDialog options and to resolve a status label.
 *
 * @returns {Array<{text: string, value: string}>} The translated statuses.
 */
export function getRefundStatuses() {
  return [
    { text: i18n.t('refund.statusPending'), value: 'pending' },
    { text: i18n.t('refund.statusRefunded'), value: 'refunded' },
    { text: i18n.t('refund.statusFailed'), value: 'failed' },
  ]
}

/**
 * Resolves the translated label for a refund status, falling back to the raw value.
 *
 * @param {string} status - The refund status value.
 * @returns {string} The translated label, or the raw status when unknown.
 */
export function getRefundStatusTranslation(status) {
  const match = getRefundStatuses().find((refundStatus) => refundStatus.value === status)
  return match ? match.text : status
}

/**
 * Sums the refunded quantities across a list of order/refund item lines.
 *
 * @param {Array<{refunded_quantity?: number}>} items - The item lines.
 * @returns {number} The total refunded quantity, or 0 when the input is not an array.
 */
export function getNbProductsRefunded(items) {
  if (!Array.isArray(items)) {
    return 0
  }
  return items.reduce((total, productLine) => total + (productLine.refunded_quantity || 0), 0)
}

/**
 * Allowed refund status transitions, keyed by the current status. A refund can only move
 * forward to a coherent next status (plus a retry from `failed` back to `pending`); a refunded
 * refund is terminal. The current status is never a valid target, so an X→X transition is never
 * proposed. Single source of truth shared by RefundStatusMenu (which targets to offer) and
 * RefundDetail (whether to show the change-status action at all).
 *
 * @param {string} currentStatus - The refund's current status (pending | refunded | failed).
 * @returns {string[]} The statuses the refund may transition to (empty when terminal/unknown).
 */
export function getAllowedRefundStatusTransitions(currentStatus) {
  const transitions = {
    pending: ['refunded', 'failed'],
    failed: ['refunded', 'pending'],
    refunded: [],
  }
  return transitions[currentStatus] ?? []
}

/**
 * Maps a transaction status to its pale Vuetify background color class. Paired with
 * getTransactionStatusTextColor to render transaction status chips with the same
 * tinted-background convention as the refunds list (getRefundStatusBackgroundColor).
 *
 * @param {string} status - The transaction status (pending | success | failure | error).
 * @returns {string} The Vuetify background color class.
 */
export function getTransactionStatusBackgroundColor(status) {
  switch (status) {
    case 'pending':
      return 'orange lighten-5'
    case 'success':
      return 'green lighten-5'
    case 'failure':
    case 'error':
      return 'red lighten-5'
    default:
      return 'grey lighten-3'
  }
}

/**
 * Maps a transaction status to its saturated Vuetify text color class, paired with
 * getTransactionStatusBackgroundColor for the transaction status chip label.
 *
 * @param {string} status - The transaction status (pending | success | failure | error).
 * @returns {string} The Vuetify text color class.
 */
export function getTransactionStatusTextColor(status) {
  switch (status) {
    case 'pending':
      return 'orange darken-4'
    case 'success':
      return 'green darken-4'
    case 'failure':
    case 'error':
      return 'red darken-1'
    default:
      return 'grey darken-4'
  }
}

/**
 * Resolves the translated label for a transaction status, falling back to the raw value.
 *
 * @param {string} status - The transaction status value.
 * @returns {string} The translated label, or the raw status when unknown.
 */
export function getTransactionStatusTranslation(status) {
  const labels = {
    pending: i18n.t('refund.txStatusPending'),
    success: i18n.t('refund.txStatusSuccess'),
    failure: i18n.t('refund.txStatusFailure'),
    error: i18n.t('refund.txStatusError'),
  }
  return labels[status] ?? status
}

/**
 * A refund amount is computed from the items' optional exclude-tax amounts and may be
 * absent or non-finite (NaN) in DB. Treat only a finite number as defined so a missing
 * amount never reaches the formatPrice filter (renders a placeholder instead of "NaN").
 * Single source of truth shared by the refunds list and the refund detail components.
 *
 * @param {unknown} amount - The raw refund amount.
 * @returns {boolean} Whether the amount is a usable number.
 */
export function isAmountDefined(amount) {
  return Number.isFinite(amount)
}

/**
 * Currency code displayed next to formatted amounts. Single source shared by the refund detail
 * components (header, articles/fees, transactions) so the currency label stays consistent.
 *
 * @param {{code?: string}|null} currency - The currency object.
 * @returns {string} The currency code, or '' when absent.
 */
export function getCurrencyCode(currency) {
  return currency ? currency.code : ''
}

/**
 * Pale Vuetify background color class for a refund fee-type chip (same tinted-background /
 * saturated-text convention as the status chips). Paired with getRefundFeeTypeTextColor.
 *
 * @param {string} type - The fee type (return | transport | other).
 * @returns {string} The Vuetify background color class.
 */
export function getRefundFeeTypeBackgroundColor(type) {
  switch (type) {
    case 'return':
      return 'purple lighten-5'
    case 'transport':
      return 'blue lighten-5'
    default:
      return 'grey lighten-3'
  }
}

/**
 * Saturated Vuetify text color class for a refund fee-type chip, paired with
 * getRefundFeeTypeBackgroundColor.
 *
 * @param {string} type - The fee type (return | transport | other).
 * @returns {string} The Vuetify text color class.
 */
export function getRefundFeeTypeTextColor(type) {
  switch (type) {
    case 'return':
      return 'purple darken-2'
    case 'transport':
      return 'blue darken-2'
    default:
      return 'grey darken-2'
  }
}
